import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BidResponse } from '../../api/endpoints/admin';
import { curr } from '../../utils/price';
import { buildAwardSummary } from './awardSummary';
import styles from './AwardSummary.module.css';

interface Props {
  bid: BidResponse;
  bids: BidResponse[];
  currentPostingCycle: number | null | undefined;
  /** Optional lane history line (#300 LaneHint); renders nothing when absent. */
  laneHint?: ReactNode;
}

/** Body of the award confirmation: the agreed prices before ops confirm (#248). */
export function AwardSummary({ bid, bids, currentPostingCycle, laneHint }: Props) {
  const { t } = useTranslation();
  const s = buildAwardSummary(bid, bids, currentPostingCycle);
  const margin =
    s.marginRupees != null ? [curr(s.marginRupees), s.marginPct].filter(Boolean).join(' · ') : null;
  return (
    <div className={styles.summary}>
      <div className={styles.rows}>
        <div className={styles.row}>
          <span className={styles.label}>{t('shipments.awardSummary.carrierGets')}</span>
          <span className={styles.amount}>{curr(s.carrierGets)}</span>
        </div>
        {s.shipperPays != null && (
          <div className={styles.row}>
            <span className={styles.label}>{t('shipments.awardSummary.shipperPays')}</span>
            <span className={styles.amount}>{curr(s.shipperPays)}</span>
          </div>
        )}
        {margin && (
          <div className={styles.row}>
            <span className={styles.label}>{t('shipments.awardSummary.margin')}</span>
            <span className={styles.amount}>{margin}</span>
          </div>
        )}
        {s.pickup && (
          <div className={styles.row}>
            <span className={styles.label}>{t('shipments.awardSummary.pickup')}</span>
            <span className={styles.value}>{s.pickup}</span>
          </div>
        )}
        {s.note && (
          <div className={styles.row}>
            <span className={styles.label}>{t('shipments.awardSummary.note')}</span>
            <span className={styles.value}>{s.note}</span>
          </div>
        )}
      </div>
      {laneHint}
      <p className={styles.footer}>
        {s.otherOpenBids > 0
          ? `Accepting rejects the other ${s.otherOpenBids} ${s.otherOpenBids === 1 ? 'bid' : 'bids'} and notifies both parties.`
          : 'Accepting notifies both parties.'}
      </p>
    </div>
  );
}
